import { authSelector } from "@myeway/auth"
import { useSelector } from "react-redux"
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom"
import "@/i18n"
import "@scss/styles.scss"
import "@components/PlayerCard/PlayerCard.scss"
import "react-bootstrap-typeahead/css/Typeahead.css"
import "react-bootstrap-typeahead/css/Typeahead.bs5.css"
import "react-calendar/dist/Calendar.css"
import ErrorRoute from "./routes/Error"
import UnauthenticatedRoute from "./routes/Unauthenticated"
import HelpRoute from "./routes/Help"
import CalendarRoute from "./routes/Calendar"
import VideoPlayerRoute from "./routes/VideoPlayer"
import MainRoute from "./routes/Main"
import AdminRoute from "./routes/Admin"
import WellbeingRoute from "./routes/Wellbeing"
import FFAClubSupportRoute from "./routes/FFAClubSupport"
import ConfirmModal from "@components/Common/ConfirmModal"
import PopupsHandler from "@components/Markdown/PopupsHandler"
import Loading from "@views/Loading"
import { lazy, Suspense } from "react"
import { lazyRetry } from "./routes/utils"
import usePrevious from "@utils/usePrevious"
import { getRedirectionSearchParam } from "./routes/utils"
import { RateLimitedModal } from "./modals/RateLimitedModal.tsx"

const SettingHandler = lazy(() => lazyRetry(() => import("./components/Navigation/SettingHandler"), "settingHandler"))
const NotFound = lazy(() => lazyRetry(() => import("./routes/NotFound.tsx"), "notFound"))

/**
 * Wraps the entire tree to provide visibility handling while unauthenticated or authenticated. Routes are only registered in specific conditions to function with refreshing access token, being logged out, or being logged in.
 * It leverages `AuthSlice` for being the source of truth for the state of authentication. Here are some common statuses:
 *    - mfa: User is required to do MFA challenge. User cannot exit this route unless they input a correct MFA or abandon the MFA login.
 *    - unauthenticated: User can only see routes like login, password reset, etc.
 *    - idle: Effectively the same as unauthenticated. Represents the state when user is new to the frontend.
 *    - loading: When login endpoint has been called, and is yet to be finished processing the request.
 *    - refreshing: When refresh endpoint has been called, and is yet to be finished processing the request.
 *    - authenticated: User can only access places they can have access to after logged in. Note that the specifics (e.g. admin routes) are still handled by independent wrappers. Attempting to access `/login` while in this status redirects the user back to the front page.
 */
const App = () => {
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const { status } = useSelector(authSelector)
	const previousStatus = usePrevious(status)

	// If we used to be logged in and are not anymore (i.e. the user logged out), navigate to /login so there can't be a redirection search param
	if (previousStatus === "authenticated" && (status === "unauthenticated" || status === "idle")) {
		return <Navigate to={"/login"} />
	}

	if (status !== "authenticated" && status !== "refreshing") {
		// If MFA challenge is required, force user to be there until they abandon or the MFA login expires
		if (status === "mfa" && location.pathname !== "/mfa-challenge") {
			return <Navigate to={"/mfa-challenge"} />
		}

		// If user is still in a route meant for authenticated users, force them to login and add redirection to where they used to be
		if (status !== "mfa" && status !== "loading" && location.pathname == "/mfa-challenge") {
			return (
				<Navigate
					to={{
						pathname: "/login",
						search: getRedirectionSearchParam(location),
					}}
				/>
			)
		}

		return (
			<Suspense>
				<Routes>
					{/* Error logout handler */}
					{ErrorRoute}

					{/* Unauthenticated routes*/}
					{UnauthenticatedRoute}

					<Route
						path="*"
						element={
							<Navigate
								to={{
									pathname: "/login",
									search: getRedirectionSearchParam(location),
								}}
							/>
						}
					/>
				</Routes>
			</Suspense>
		)
	}

	const redirect = searchParams.get("r") || "/"
	const decodedRedirect = decodeURIComponent(redirect)

	const actualRedirectRoute = !decodedRedirect.startsWith("/") ? "/" : decodedRedirect
	// Handle redirecting user to where they into the /login route initially
	// Main benefit of this is to allow users to get where they want to go (for example: after clicking a new.myeway.fi link on Teams) after they have sufficiently logged in
	if (location.pathname === "/login" || location.pathname === "/mfa-challenge") {
		return <Navigate to={actualRedirectRoute} />
	}

	return (
		<Suspense fallback={<Loading />}>
			<SettingHandler />
			<Routes>
				{/* Error logout handler */}
				{ErrorRoute}

				{/* Unauthenticated routes*/}
				{UnauthenticatedRoute}

				{/* Custom layouts (side panels etc.) */}
				{HelpRoute}
				{CalendarRoute}
				{VideoPlayerRoute}

				{/* Main layout routes */}
				{MainRoute}

				{/* Wellbeing-restricted routes */}
				{WellbeingRoute}

				{/* Admin-only routes */}
				{AdminRoute}

				{/* Finnish Football Association's Club Support routes */}
				{FFAClubSupportRoute}

				<Route path="*" element={<NotFound />} />
			</Routes>
			<ConfirmModal />
			<PopupsHandler />
			<RateLimitedModal />
		</Suspense>
	)
}

export default App
